// components/AttendeesList.js

import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { theme } from '../theme';
import { statusTagVariants } from '../utils/statusColors';
import { getAllStates, useAttendeeStateTranslations } from '../constants/attendeeStates';
import Button from './Button/Button';
import Tag from './Tag';
import Typography from '../components/Typography';
import Tooltip from './Tooltip/Tooltip';
import AttendeeDetailWrapper from './AttendeeDetailWrapper';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import HelmetIcon from './Icons/HelmetIcon';
import SearchIcon from './Icons/SearchIcon';
import CloseIcon from './Icons/CloseIcon';
import RevertIcon from './Icons/RevertIcon';
import ChevronRightIcon from './Icons/ChevronRightIcon';
import FilterSolidIcon from './Icons/FilterSolidIcon';
import InputText from './Input/InputText';
import FilterDropdown from './FilterDropdown';
import SortDropdown from './SortDropdown';
import Modal from './Modal/Modal';
import Chip from './Chip';
import useWindowDimensions from '../hooks/useWindowDimensions';
import { useTranslation } from 'react-i18next';
import { TICKET_FREE } from '../constants/eventTypes';

const AttendeesList = ({
  attendees,
  needsVehicle,
  onApprove,
  onReject,
  onRejectWithMessage,
  onDenyCancellation,
  onAcceptCancellation,
  onMarkRefunded,
  onCancelByOrganizer,
  deepLinkedAttendeeId = null,
  onAttendeeOpen,
  onAttendeeDrawerClose
}) => {
  const { t } = useTranslation('manageEventAttendees');
  const { getStateLabel } = useAttendeeStateTranslations();
  const { width } = useWindowDimensions();
  const isMobile = width < parseInt(theme.breakpoints.md);
  const [selectedAttendee, setSelectedAttendee] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  // Estados para filtros y búsqueda
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [selectedTicketTypes, setSelectedTicketTypes] = useState([]);
  const [sortBy, setSortBy] = useState('date');

  // Estados para filtros móviles
  const [isFiltersModalOpen, setIsFiltersModalOpen] = useState(false);
  const [tempSelectedStatuses, setTempSelectedStatuses] = useState([]);
  const [tempSelectedTicketTypes, setTempSelectedTicketTypes] = useState([]);
  const [tempSortBy, setTempSortBy] = useState('date');

  // Obtener estados únicos que existen en los datos y son válidos
  const existingStatuses = useMemo(() => {
    const statusSet = new Set();
    const validStates = getAllStates();
    attendees.forEach(attendee => {
      if (attendee.status && validStates.includes(attendee.status)) {
        statusSet.add(attendee.status);
      }
    });
    return Array.from(statusSet);
  }, [attendees]);

  // Opciones de estado basadas en datos existentes
  const statusOptions = useMemo(() => {
    return existingStatuses.map(status => ({
      value: status,
      label: getStateLabel(status)
    }));
  }, [existingStatuses, getStateLabel]);

  // Obtener tipos de ticket únicos
  const ticketTypes = useMemo(() => {
    const uniqueTickets = [];
    const seenTickets = new Set();

    attendees.forEach(attendee => {
      const ticketKey = `${attendee.ticketId._id}`;
      if (!seenTickets.has(ticketKey)) {
        seenTickets.add(ticketKey);
        uniqueTickets.push({
          value: attendee.ticketId.type,
          label: `${attendee.ticketId.name} (${attendee.ticketId.type === TICKET_FREE ? t('common:free') : `${attendee.ticketId.price}€`})`
        });
      }
    });

    return uniqueTickets;
  }, [attendees, t]);

  // Verificar si hay más de un tipo de ticket
  const hasMultipleTicketTypes = ticketTypes.length > 1;

  // Filtrar y ordenar asistentes
  const filteredAndSortedAttendees = useMemo(() => {
    let filtered = [...attendees];

    // Filtrar por término de búsqueda
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(attendee => {
        const fullName = `${attendee.userId.name} ${attendee.userId.lastName}`.toLowerCase();
        const email = attendee.userId.email.toLowerCase();
        const vehicle = attendee.vehicleId ?
          `${attendee.vehicleId.brand} ${attendee.vehicleId.model}`.toLowerCase() : '';

        return fullName.includes(searchLower) ||
          email.includes(searchLower) ||
          vehicle.includes(searchLower);
      });
    }

    // Filtrar por estados seleccionados
    if (selectedStatuses.length > 0) {
      filtered = filtered.filter(attendee => selectedStatuses.includes(attendee.status));
    }

    // Filtrar por tipos de ticket seleccionados
    if (selectedTicketTypes.length > 0) {
      filtered = filtered.filter(attendee => selectedTicketTypes.includes(attendee.ticketId.type));
    }

    // Ordenar
    filtered.sort((a, b) => {
      let comparison = 0;

      if (sortBy === 'date') {
        comparison = new Date(a.createdAt) - new Date(b.createdAt);
      } else if (sortBy === 'name') {
        const nameA = `${a.userId.name} ${a.userId.lastName}`.toLowerCase();
        const nameB = `${b.userId.name} ${b.userId.lastName}`.toLowerCase();
        comparison = nameA.localeCompare(nameB);
      }

      return sortBy === 'name' ? comparison : -comparison; // Nombre ascendente, fecha descendente por defecto
    });

    return filtered;
  }, [attendees, searchTerm, selectedStatuses, selectedTicketTypes, sortBy]);

  // Actualizar selectedAttendee cuando cambie la lista de attendees
  useEffect(() => {
    if (selectedAttendee && attendees) {
      const updatedAttendee = attendees.find(attendee => attendee._id === selectedAttendee._id);
      if (updatedAttendee) {
        setSelectedAttendee(updatedAttendee);
      }
    }
  }, [attendees, selectedAttendee]);

  useEffect(() => {
    if (!deepLinkedAttendeeId) {
      setIsDrawerOpen(false);
      setSelectedAttendee(null);
      return;
    }

    const targetAttendee = attendees.find(attendee => attendee._id === deepLinkedAttendeeId);

    if (!targetAttendee) {
      return;
    }

    setSelectedAttendee(targetAttendee);
    setIsDrawerOpen(true);
  }, [attendees, deepLinkedAttendeeId]);

  const handleAttendeeClick = (attendee) => {
    setSelectedAttendee(attendee);
    setIsDrawerOpen(true);
    if (typeof onAttendeeOpen === 'function') {
      onAttendeeOpen(attendee._id);
    }
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
    setSelectedAttendee(null);
    if (typeof onAttendeeDrawerClose === 'function') {
      onAttendeeDrawerClose();
    }
  };

  // Funciones para manejar filtros
  const handleStatusChange = (newStatuses) => {
    setSelectedStatuses(newStatuses);
  };

  const handleTicketTypeChange = (newTicketTypes) => {
    setSelectedTicketTypes(newTicketTypes);
  };

  const handleSortChange = (newSortBy) => {
    setSortBy(newSortBy);
  };

  // Opciones de ordenamiento
  const sortOptions = [
    { value: 'date', label: t('attendeesList.sorting.registrationDate') },
    { value: 'name', label: t('attendeesList.sorting.name') }
  ];

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedStatuses([]);
    setSelectedTicketTypes([]);
    setSortBy('date');
  };

  const hasActiveFilters = searchTerm || selectedStatuses.length > 0 || selectedTicketTypes.length > 0;
  const activeFiltersCount = selectedStatuses.length + selectedTicketTypes.length;

  // Funciones para modal de filtros móvil
  const openFiltersModal = () => {
    setTempSelectedStatuses([...selectedStatuses]);
    setTempSelectedTicketTypes([...selectedTicketTypes]);
    setTempSortBy(sortBy);
    setIsFiltersModalOpen(true);
  };

  const closeFiltersModal = () => {
    setIsFiltersModalOpen(false);
  };

  const applyFilters = () => {
    setSelectedStatuses([...tempSelectedStatuses]);
    setSelectedTicketTypes([...tempSelectedTicketTypes]);
    setSortBy(tempSortBy);
    setIsFiltersModalOpen(false);
  };

  const resetFiltersInModal = () => {
    setTempSelectedStatuses([]);
    setTempSelectedTicketTypes([]);
    setTempSortBy('date');
  };

  const handleStatusChipToggle = (status) => {
    setTempSelectedStatuses(prev =>
      prev.includes(status)
        ? prev.filter(s => s !== status)
        : [...prev, status]
    );
  };

  const handleTicketChipToggle = (ticketType) => {
    setTempSelectedTicketTypes(prev =>
      prev.includes(ticketType)
        ? prev.filter(t => t !== ticketType)
        : [...prev, ticketType]
    );
  };

  return (
    <ListWrapper>
      <HeaderSection>
        <Typography $variant="title-3-semibold" $variantTablet="title-4-semibold" $variantMobile="body-1-semibold" as="h2">
          {t('attendeesList.title', {
            count: filteredAndSortedAttendees.length,
            total: attendees.length !== filteredAndSortedAttendees.length ? attendees.length : null
          })}
        </Typography>
        {attendees.length > 0 && (
          <FilterSection>

            {/* Barra de búsqueda */}
            <SearchContainer>
              <SearchInputWrapper>
                <SearchIconWrapper>
                  <SearchIcon fill={theme.colors.defaultWeak} width={16} height={16} />
                </SearchIconWrapper>
                <StyledInputText
                  type="text"
                  placeholder={t('attendeesList.search.placeholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {searchTerm && (
                  <ClearButton onClick={() => setSearchTerm('')}>
                    <CloseIcon fill={theme.colors.defaultStrong} width={16} height={16} />
                  </ClearButton>
                )}
              </SearchInputWrapper>
            </SearchContainer>

            {/* Filtros y ordenamiento */}
            {!isMobile && attendees.length > 0 ? (
              // Filtros de escritorio
              <FiltersContainer>
                <FiltersBlock>
                  {/* Filtros de estado */}
                  <FilterDropdown
                    // label={t('attendeesList.filters.allStatuses')}
                    options={statusOptions}
                    selectedValues={selectedStatuses}
                    onSelectionChange={handleStatusChange}
                    placeholder={t('attendeesList.filters.statusPlaceholder')}
                  />

                  {/* Filtros de tipo de ticket - solo mostrar si hay más de un tipo */}
                  {hasMultipleTicketTypes && (
                    <FilterDropdown
                      label={t('attendeesList.filters.ticketLabel')}
                      options={ticketTypes}
                      selectedValues={selectedTicketTypes}
                      onSelectionChange={handleTicketTypeChange}
                      placeholder={t('attendeesList.filters.ticketPlaceholder')}
                      showIcon={false}
                    />
                  )}
                </FiltersBlock>
                <FiltersBlock>
                  {/* Ordenamiento */}
                  <SortDropdown
                    label={t('attendeesList.sorting.label')}
                    options={sortOptions}
                    selectedValue={sortBy}
                    onSelectionChange={handleSortChange}
                    placeholder={t('attendeesList.sorting.registrationDate')}
                  />

                  {hasActiveFilters && (
                    <Button
                      $variant=""
                      size="l"
                      sizeTablet="l"
                      sizeMobile="m"
                      onClick={clearFilters}
                    >
                      {t('attendeesList.filters.clearFilters')}
                    </Button>
                  )}
                </FiltersBlock>
              </FiltersContainer>
            ) : (
              // Filtros móviles
              <MobileFiltersContainer>
                <MobileFiltersRow>
                  <MobileFilterButton onClick={openFiltersModal}>
                    <FilterSolidIcon fill={theme.colors.defaultStrong} width={16} height={16} />
                    <Typography $variant="body-2-medium">
                      {t('attendeesList.filters.filters')}
                    </Typography>
                    {activeFiltersCount > 0 && (
                      <FilterCount>
                        <Typography $variant="caption-semibold" color={theme.colors.inverseMain}>
                          {activeFiltersCount}
                        </Typography>
                      </FilterCount>
                    )}
                  </MobileFilterButton>
                  {hasActiveFilters && (
                    <Button
                      $variant=""
                      size="m"
                      onClick={clearFilters}
                    >
                      {t('attendeesList.filters.clearFilters')}
                    </Button>
                  )}
                </MobileFiltersRow>
              </MobileFiltersContainer>
            )}
          </FilterSection>
        )}
      </HeaderSection>
      {/* statusTagVariants
      <Tag $variant='success'>Asistirá</Tag>
      <Tag $variant='warning'>Pendiente</Tag>
      <Tag $variant='danger'>Rechazado</Tag>
      <Tag $variant='danger'>No asistirá</Tag>
      <Tag $variant='warning'>Devolución solicitada</Tag>
      <Tag $variant='subtle'>Devolucion denegada</Tag>
      <Tag $variant='subtle'>Reembolsado</Tag>
      <Tag $variant='danger'>Cancelado (organizador)</Tag>
      <Tag $variant='danger'>Cancelado (usuario)</Tag>
      ----
      <Tag $variant='brandSubtle'>Label</Tag>
      <Tag $variant='brand'>Label</Tag>
      <Tag $variant='danger'>Label</Tag>
      <Tag $variant='outline'>Label</Tag>
      <Tag $variant='inverse'>Label</Tag>
      <Tag $variant='blur'>Label</Tag>
      <Tag $variant='subtle'>Subtle</Tag>
      <Tag $variant='default'>Label</Tag>
      <Tag $variant='transparent'>Label</Tag>
      <Tag $variant='blur'>Label</Tag> */}


      {filteredAndSortedAttendees.length > 0 ? (
        <GridTable>
          {/* Header Row */}
          <GridHeaderCell $area="name">
            <Typography as="p" $variant="overline-semibold" color={theme.colors.defaultWeak} $textTransform="uppercase">
              {t('attendeesList.table.headers.name')}
            </Typography>
          </GridHeaderCell>
          <GridHeaderCell $area="ticket">
            <Typography as="p" $variant="overline-semibold" color={theme.colors.defaultWeak} $textTransform="uppercase">
              {t('attendeesList.table.headers.ticket')}
            </Typography>
          </GridHeaderCell>
          <GridHeaderCell $area="date">
            <Typography as="p" $variant="overline-semibold" color={theme.colors.defaultWeak} $textTransform="uppercase">
              {t('attendeesList.table.headers.registrationDate')}
            </Typography>
          </GridHeaderCell>
          <GridHeaderCell $area="status">
            <Typography as="p" $variant="overline-semibold" color={theme.colors.defaultWeak} $textTransform="uppercase">
              {t('attendeesList.table.headers.status')}
            </Typography>
          </GridHeaderCell>
          <GridHeaderCell $area="actions"></GridHeaderCell>

          {/* Data Rows */}
          {filteredAndSortedAttendees.map((attendee, index) => {
            const isLastRow = index === filteredAndSortedAttendees.length - 1;
            return (
              <React.Fragment key={attendee._id}>
                {/* Desktop Grid Cells */}
                <GridRow>
                  <GridDataCell $area="name" $row={index + 2} $isLastRow={isLastRow} onClick={() => handleAttendeeClick(attendee)} style={{ cursor: 'pointer' }}>
                    {attendee.userId.userAvatar ? (
                      <Avatar src={attendee.userId.userAvatar} alt={`${attendee.userId.name} avatar`} />
                    ) : (
                      <AvatarPlaceholder>
                        <HelmetIcon width={32} height={32} fill={theme.colors.defaultStrong} />
                      </AvatarPlaceholder>
                    )}
                    <AttendeeData>
                      <Typography as="p" $variant="body-2-semibold">
                        {attendee.userId.name} {attendee.userId.lastName}
                      </Typography>
                      <Typography as="p" $variant="body-3-regular">
                        {attendee.userId.email}
                      </Typography>
                      {needsVehicle && (
                        <Typography as="p" $variant="body-3-regular">
                          {attendee.vehicleId ? attendee.vehicleId.brand : '-'} {attendee.vehicleId ? attendee.vehicleId.model : '-'}
                        </Typography>
                      )}
                    </AttendeeData>
                  </GridDataCell>
                  <GridDataCell $area="ticket" $row={index + 2} $isLastRow={isLastRow} onClick={() => handleAttendeeClick(attendee)} style={{ cursor: 'pointer' }}>
                    {/* <Typography as="p" $variant="body-3-regular">
                    {attendee.ticketId.name}
                  </Typography> */}
                    <Typography as="p" $variant="body-3-regular">
                      {attendee.ticketId.name} ({attendee.ticketId.type === TICKET_FREE ? t('attendeeDetailDrawer.common.free') : `${attendee.ticketId.price}€`})
                    </Typography>
                  </GridDataCell>
                  <GridDataCell $area="date" $row={index + 2} $isLastRow={isLastRow} onClick={() => handleAttendeeClick(attendee)} style={{ cursor: 'pointer' }}>
                    <Typography as="p" $variant="body-3-regular">
                      {format(new Date(attendee.createdAt), 'dd/MM/yyyy', { locale: es })}
                    </Typography>
                  </GridDataCell>
                  <GridDataCell $area="status" $row={index + 2} $isLastRow={isLastRow} onClick={() => handleAttendeeClick(attendee)} style={{ cursor: 'pointer' }}>
                    <Tooltip
                      text={(attendee.status === 'cancellation_pending' || attendee.status === 'cancelled_by_user') && attendee.userMessage ? attendee.userMessage : getStateLabel(attendee.status)}
                      trigger="hover"
                    >
                      <Tag $variant={statusTagVariants[attendee.status]}>
                        {getStateLabel(attendee.status)}
                      </Tag>
                    </Tooltip>
                  </GridDataCell>
                  <GridDataCell $area="actions" $row={index + 2} $isLastRow={isLastRow} onClick={() => handleAttendeeClick(attendee)} style={{ cursor: 'pointer' }}>
                    <ChevronRightIcon fill={theme.colors.defaultMain} />
                  </GridDataCell>
                </GridRow>

                {/* Mobile Row Container */}
                <MobileRowContainer $isLastRow={isLastRow} onClick={() => handleAttendeeClick(attendee)} style={{ cursor: 'pointer' }}>
                  <MobileAttendeeCard>
                    <AttendeeHeader>
                      <AttendeeBlock>
                        {attendee.userId.userAvatar ? (
                          <Avatar src={attendee.userId.userAvatar} alt={`${attendee.userId.name} avatar`} />
                        ) : (
                          <AvatarPlaceholder>
                            <HelmetIcon width={32} height={32} fill={theme.colors.defaultStrong} />
                          </AvatarPlaceholder>
                        )}
                        <AttendeeInfo>
                          <Typography as="p" $variant="body-2-semibold" $truncate>
                            {attendee.userId.name} {attendee.userId.lastName}
                          </Typography>
                          <Typography as="p" $variant="body-3-medium" color={theme.colors.defaultWeak} $truncate>
                            {attendee.userId.email}
                          </Typography>
                        </AttendeeInfo>
                      </AttendeeBlock>
                      {/* Acciones movidas al drawer */}

                    </AttendeeHeader>
                    <AttendeeRegistration>
                      <RegistrationRow>
                        <RegistrationInfo>
                          <Typography style={{ width: '60px' }} as="span" $variant="body-3-medium" color={theme.colors.defaultWeak}>{t('attendeesList.mobile.registration')}</Typography>
                          <Typography as="span" $variant="body-3-medium" color={theme.colors.defaultStrong}>
                            {attendee.ticketId.name} ({attendee.ticketId.type === TICKET_FREE ? t('attendeeDetailDrawer.common.free') : `${attendee.ticketId.price}€`})<br />
                            {format(new Date(attendee.createdAt), 'dd/MM/yyyy', { locale: es })}
                          </Typography>
                        </RegistrationInfo>

                        {needsVehicle && (
                          <RegistrationVehicleInfo>
                            <Typography style={{ width: '60px' }} as="span" $variant="body-3-medium" color={theme.colors.defaultWeak}>{t('attendeesList.mobile.vehicle')}:</Typography>
                            <Typography as="p" $variant="body-3-medium" color={theme.colors.defaultStrong}>
                              {attendee.vehicleId ? attendee.vehicleId.brand : '-'} {attendee.vehicleId ? attendee.vehicleId.model : '-'}
                            </Typography>
                          </RegistrationVehicleInfo>
                        )}

                      </RegistrationRow>
                      <Tag $variant={statusTagVariants[attendee.status]}>
                        {getStateLabel(attendee.status)}
                      </Tag>
                      {(attendee.status === 'cancellation_pending' || attendee.status === 'cancelled_by_user') && attendee.userMessage && (
                        <Typography as="p" $variant="body-3-regular" color={theme.colors.defaultStrong} style={{ marginTop: '8px' }} >
                          "{attendee.userMessage}"
                        </Typography>
                      )}
                    </AttendeeRegistration>

                    {/* Acciones movidas al drawer */}
                  </MobileAttendeeCard>
                </MobileRowContainer>
              </React.Fragment>
            );
          })}
        </GridTable>
      ) : (
        <EmptyMessage>
          <MessageContainer>
            <EmptyMedia>
              <EmptyImg src="/icons/attendees-solid.svg" alt="empty state icon" />
            </EmptyMedia>
            <Typography as="p" $variant="body-1-semibold" $variantTablet="body-2-semibold" $variantMobile="body-2-semibold" color={theme.colors.defaultWeak}>
              {hasActiveFilters ? t('attendeesList.emptyState.noResults') : t('attendeesList.emptyState.noAttendees')}
            </Typography>
            <Typography
              as="p"
              $variant="body-2-regular"
              color={theme.colors.defaultWeak}
              style={{ maxWidth: '280px' }}
              $align="center"
            >
              {hasActiveFilters
                ? t('attendeesList.emptyState.noResultsDescription')
                : t('attendeesList.emptyState.noAttendeesDescription')}
            </Typography>
          </MessageContainer>
        </EmptyMessage>
      )
      }

      <AttendeeDetailWrapper
        isOpen={isDrawerOpen}
        onClose={handleCloseDrawer}
        attendee={selectedAttendee}
        onApprove={onApprove}
        onReject={onRejectWithMessage || onReject}
        onDenyCancellation={onDenyCancellation}
        onAcceptCancellation={onAcceptCancellation}
        onMarkRefunded={onMarkRefunded}
        onCancelByOrganizer={onCancelByOrganizer}
      />

      {/* Modal de filtros móvil */}
      {isFiltersModalOpen && (
        <Modal
          onClose={closeFiltersModal}
          title={t('attendeesList.filters.filtersAndSorting')}
        >
          <FiltersModalContent>
            {/* Estados de inscripción */}
            {attendees.length > 0 && (
              <FilterBlock>
                <Typography $variant="body-2-semibold" color={theme.colors.defaultStrong}>
                  {t('attendeesList.filters.enrollmentStatuses')}
                </Typography>
                <ChipsContainer>
                  {statusOptions.map((status) => (
                    <Chip
                      key={status.value}
                      category={status.label}
                      isActive={tempSelectedStatuses.includes(status.value)}
                      onClick={() => handleStatusChipToggle(status.value)}
                    />
                  ))}
                </ChipsContainer>
              </FilterBlock>
            )}

            {/* Tipos de ticket */}
            {hasMultipleTicketTypes && (
              <FilterBlock>
                <Typography $variant="body-2-semibold" color={theme.colors.defaultStrong}>
                  {t('attendeesList.filters.tickets')}
                </Typography>
                <ChipsContainer>
                  {ticketTypes.map((ticket) => (
                    <Chip
                      key={ticket.value}
                      category={ticket.label}
                      isActive={tempSelectedTicketTypes.includes(ticket.value)}
                      onClick={() => handleTicketChipToggle(ticket.value)}
                    />
                  ))}
                </ChipsContainer>
              </FilterBlock>
            )}

            {/* Ordenamiento */}
            <FilterBlock>
              <Typography $variant="body-2-semibold" color={theme.colors.defaultStrong}>
                {t('attendeesList.sorting.label')}
              </Typography>
              <SortingContainer>
                {sortOptions.map((option) => (
                  <SortingOption
                    key={option.value}
                    $selected={tempSortBy === option.value}
                    onClick={() => setTempSortBy(option.value)}
                  >
                    <Typography
                      $variant="body-2-semibold"
                      color={tempSortBy === option.value ? theme.colors.brandMain : theme.colors.defaultSubtle}
                    >
                      {option.label}
                    </Typography>
                  </SortingOption>
                ))}
              </SortingContainer>
            </FilterBlock>

            {/* Botones de acción */}
            <ModalActions>
              <Button
                $variant="ghost"
                size="l"
                sizeTablet="l"
                sizeMobile="m"
                onClick={resetFiltersInModal}
              >
                <RevertIcon fill={theme.colors.defaultMain} height={16} width={16} />
                {t('attendeesList.filters.clearFilters')}
              </Button>
              <Button
                $variant=""
                size="l"
                sizeTablet="l"
                sizeMobile="m"
                onClick={applyFilters}
              >
                {t('attendeesList.filters.applyFilters')}
              </Button>
            </ModalActions>
          </FiltersModalContent>
        </Modal>
      )}
    </ListWrapper >
  );
};

// La función getStatusLabel ahora se importa desde las constantes centralizadas

AttendeesList.propTypes = {
  attendees: PropTypes.array.isRequired,
  onApprove: PropTypes.func.isRequired,
  onReject: PropTypes.func.isRequired,
  onDenyCancellation: PropTypes.func.isRequired,
  onAcceptCancellation: PropTypes.func.isRequired,
  onMarkRefunded: PropTypes.func.isRequired,
  deepLinkedAttendeeId: PropTypes.string,
  onAttendeeOpen: PropTypes.func,
  onAttendeeDrawerClose: PropTypes.func,
};

export default AttendeesList;

// Estilos
const ListWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.sm};
  width: 100%;
  padding-bottom: ${({ theme }) => theme.sizing.xl};
  padding-top: ${({ theme }) => theme.sizing.xl};
`;

const GridTable = styled.div`
  display: grid;
  // grid-template-columns: 2fr 1.5fr 1fr 1.75fr 0.25fr;
  grid-template-columns: 2fr 1.5fr 1fr 240px 0.25fr;
  grid-template-areas:
    "name ticket date status actions";
  gap: 0;
  border: 1px solid ${({ theme }) => theme.border.defaultWeak};
  border-radius: ${({ theme }) => theme.radius.xs};
  // overflow: hidden;

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-template-columns: 2fr 1.5fr 1fr 1.75fr 0.25fr;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    display: flex;
    flex-direction: column;
    border: none;
    gap: ${({ theme }) => theme.sizing.xxs};
  }
`;

const GridRow = styled.div`
  display: contents;
  
  &:hover > div {
    background-color: ${({ theme }) => theme.fill.defaultSubtle};
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    display: block;
  }
`;

const GridHeaderCell = styled.div`
  grid-area: ${({ $area }) => $area};
  padding: 12px ${({ theme }) => theme.sizing.sm};
  background-color: ${({ theme }) => theme.fill.defaultSubtle};
  border-bottom: 1px solid ${({ theme }) => theme.border.defaultWeak};
  display: flex;
  align-items: center;

  /* Actions column specific styling */
  ${({ $area, theme }) => $area === 'actions' && `
    border-top-right-radius: ${theme.radius.xs};
  `}

  ${({ $area, theme }) => $area === 'name' && `
    border-top-left-radius: ${theme.radius.xs};
  `}

  ${({ $area, theme }) => $area === 'status' && `
    min-width: 210px;
  `}

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    display: none;
  }
`;

const GridDataCell = styled.div`
  grid-area: ${({ $area }) => $area};
  grid-row: ${({ $row }) => $row};
  padding: 12px ${({ theme }) => theme.sizing.sm};
  border-bottom: 1px solid ${({ theme }) => theme.border.defaultWeak};
  display: flex;
  align-items: center;
  transition: background-color 0.2s ease;

  /* Remove border from cells in the last row */
  ${({ $isLastRow }) => $isLastRow && `
    border-bottom: none;
  `}
  
  /* Name column specific styling */
  ${({ $area }) => $area === 'name' && `
    gap: ${theme.sizing.sm};
  `}
  
  /* Ticket column specific styling */
  ${({ $area }) => $area === 'ticket' && `
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
    gap: ${theme.sizing.xxs};
  `}
  
  /* Status column specific styling */
  ${({ $area }) => $area === 'status' && `
    flex-direction: row;
    gap: ${theme.sizing.xs};
    min-width: 210px;
  `}
  
  /* Actions column specific styling */
  ${({ $area }) => $area === 'actions' && `
    flex-direction: row;
    // gap: ${theme.sizing.xs};
    justify-content: flex-end;
    align-items: center;
  `}

  /* Hide all grid cells on mobile */
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    display: none;
  }
`;

const MobileRowContainer = styled.div`
  display: none;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    display: block;
    // margin-bottom: ${({ theme }) => theme.sizing.sm};
    
    ${({ $isLastRow }) => $isLastRow && `
      margin-bottom: 0;
    `}
  }
`;

// Removed List component - now using GridTable

const Avatar = styled.img`
  width: ${({ theme }) => theme.sizing.xl};
  height: ${({ theme }) => theme.sizing.xl};
  border-radius: ${({ theme }) => theme.radius.xs};
  border: 1px solid ${({ theme }) => theme.border.defaultMain};
  object-fit: cover;
`;

const AvatarPlaceholder = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: ${({ theme }) => theme.sizing.xl};
  height: ${({ theme }) => theme.sizing.xl};
  flex-shrink: 0;
  border-radius: ${({ theme }) => theme.radius.xs};
  border: 1px solid ${({ theme }) => theme.border.defaultMain};
  background-color: ${({ theme }) => theme.fill.defaultWeak};
`;

const AttendeeData = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.xxs};
  flex: 1;
  min-width: 0; /* Permite que el contenido se contraiga */
`;

const MobileAttendeeCard = styled.div`
  display: flex;
  flex-direction: column;
  // gap: ${({ theme }) => theme.sizing.xs};
  width: 100%;
  border: 1px solid ${({ theme }) => theme.border.defaultWeak};
  border-radius: ${({ theme }) => theme.radius.xs};
  background-color: ${({ theme }) => theme.fill.defaultMain};
  transition: all 0.2s ease;
`;

const AttendeeHeader = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: ${({ theme }) => theme.sizing.sm};
  padding: 12px;
  padding-bottom: 8px;
  justify-content: space-between;
  min-width: 0; /* Permite que el contenido se contraiga */
`;

const AttendeeBlock = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${({ theme }) => theme.sizing.xs};
  flex: 1;
  min-width: 0; /* Permite que el contenido se contraiga */
`;

const AttendeeInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 1;
  min-width: 0; /* Permite que el contenido se contraiga */
`;

const AttendeeRegistration = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.xs};
  padding: 12px;
  padding-top: 0px;
`;

const RegistrationInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  padding: 0px ${({ theme }) => theme.sizing.xs};
  flex-basis: 50%;

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    flex-basis: 100%;
  }
`;

const RegistrationVehicleInfo = styled(RegistrationInfo)`
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding-top: 8px;
  }
`;



const RegistrationRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: space-between;
  border-radius: ${({ theme }) => theme.radius.xs};
  border: 1px solid ${({ theme }) => theme.border.defaultSubtle};
  flex-wrap: wrap;
  padding: ${({ theme }) => theme.sizing.xs} 0px;
  // background-color: ${({ theme }) => theme.fill.defaultSubtle};
`;

// const AttendeeManagement = styled.div`
//   display: flex;
//   flex-direction: row;
//   align-items: center;
//   gap: ${({ theme }) => theme.sizing.xs};
//   width: 100 %;
//   border-top: 1px solid ${({ theme }) => theme.border.defaultWeak};
//   padding: ${({ theme }) => theme.sizing.sm};
// `;

const EmptyMessage = styled.div`
  padding: ${({ theme }) => theme.sizing.xxl};
  color: ${({ theme }) => theme.colors.defaultWeak};
  text-align: center;
  border: 1px solid ${({ theme }) => theme.border.defaultSubtle};
  border-radius: ${({ theme }) => theme.radius.xs};
`;

const MessageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.sizing.xxs};
`;

const EmptyMedia = styled.div`
  width: ${({ theme }) => theme.sizing.xxl};
  height: ${({ theme }) => theme.sizing.xxl};
  border-radius: ${({ theme }) => theme.radius.xs};
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${({ theme }) => theme.fill.defaultWeak};
  margin-bottom: ${({ theme }) => theme.sizing.xs};
`;

const EmptyImg = styled.img`
  width: ${({ theme }) => theme.sizing.md};;
  height: ${({ theme }) => theme.sizing.md};
`;

// const ManagementButton = styled(Button)`
//   @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
//   flex-grow: 1;
// }
// `;

// Estilos para filtros y búsqueda
const HeaderSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.md};
`;

const SearchContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.xs};
`;

const SearchInputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
`;

const SearchIconWrapper = styled.div`
  position: absolute;
  left: 12px;
  display: flex;
  align-items: center;
  pointer-events: none;
  z-index: 1;
`;

const StyledInputText = styled(InputText)`
  padding-left: 40px;
  padding-right: ${({ theme }) => theme.sizing.lg};
  width: 100%;
`;

const ClearButton = styled.button`
  position: absolute;
  right: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: background-color 0.2s ease;
  
  &:hover {
    background-color: ${({ theme }) => theme.fill.defaultSubtle};
  }
`;

const FiltersContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${({ theme }) => theme.sizing.sm};
  justify-content: space-between;
`;

const FilterSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.sm};
  align-items: stretch;
  width: 100%;
`;

const FilterBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.xs};
  align-items: stretch;
  width: 100%;
  padding: ${({ theme }) => theme.sizing.sm};
  padding-bottom: 0px;
`;

const FiltersBlock = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${({ theme }) => theme.sizing.sm};
  align-items: center;
`;

// Styled components para filtros móviles
const MobileFiltersContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.sm};
  width: 100%;
`;

const MobileFiltersRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: ${({ theme }) => theme.sizing.sm};
`;

const MobileFilterButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.sizing.xs};
  padding: 0px ${({ theme }) => theme.sizing.xs};
  background: ${({ theme }) => theme.fill.defaultMain};
  border: 1px solid ${({ theme }) => theme.border.defaultWeak};
  height: 32px;
  border-radius: ${({ theme }) => theme.radius.xs};
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;


  &:hover {
    background: ${({ theme }) => theme.fill.defaultSubtle};
  }
`;

const FilterCount = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  background: ${({ theme }) => theme.fill.inverseMain};
  border-radius: 10px;
`;

// Styled components para el modal de filtros
const FiltersModalContent = styled.div`
  display: flex;
  flex-direction: column;
  // gap: ${({ theme }) => theme.sizing.sm};
  padding: 0;
`;

const ChipsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.sizing.xs};
`;

const SortingContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.sizing.xs};
`;

const SortingOption = styled.button`
  cursor: pointer;
  border: 1px solid ${({ $selected, theme }) => ($selected ? theme.border.brandMain : theme.border.defaultWeak)};
  border-radius: ${({ theme }) => theme.sizing.lg};
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  gap: ${({ theme }) => theme.sizing.xxs};
  padding: ${({ theme }) => theme.sizing.xs} ${({ theme }) => theme.sizing.sm};
  transition: all 0.3s;
  background: transparent;
  color: ${({ $selected, theme }) => ($selected ? theme.colors.brandMain : theme.colors.defaultMain)};

  &:hover {
    border: 1px solid ${({ $selected, theme }) => ($selected ? theme.border.brandMain : theme.border.defaultWeak)};
    background-color: ${({ $selected, theme }) => ($selected ? theme.fill.brandAlphaMain16 : theme.fill.defaultWeak)};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: ${({ theme }) => theme.sizing.xxs} 12px;
  }
`;

const ModalActions = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${({ theme }) => theme.sizing.sm};
  justify-content: space-between;
  padding: ${({ theme }) => theme.sizing.sm};
  border-top: 1px solid ${({ theme }) => theme.border.defaultSubtle};
  margin-top: ${({ theme }) => theme.sizing.sm};
`;
